import { useState, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getEAArtifacts, getEAProject } from '../services/ea-api'
import type { EAArtifactListItem } from '../types/ea'
import EAArtifactCard from '../components/ea/EAArtifactCard'

const glassPanel = {
  background: 'rgba(15, 23, 42, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(100, 180, 255, 0.1)',
}

const ALL_VENDORS = ['Arista', 'PaloAlto', 'Nutanix', 'Azure', 'Cisco']

const VENDOR_COLORS: Record<string, string> = {
  Arista: '#2563eb',
  PaloAlto: '#dc2626',
  Nutanix: '#059669',
  Azure: '#0284c7',
  Cisco: '#7c3aed',
}

const PHASE_TABS: { value: string; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'requirements', label: 'Requirements' },
  { value: 'design', label: 'Design' },
  { value: 'deployment', label: 'Deployment' },
  { value: 'qc', label: 'QC' },
  { value: 'deliverables', label: 'Deliverables' },
]

const PHASE_COLORS: Record<string, string> = {
  requirements: '#3b82f6',
  design: '#8b5cf6',
  deployment: '#10b981',
  qc: '#f59e0b',
  deliverables: '#06b6d4',
}

const ARTIFACT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All Types' },
  { value: 'discovery_questionnaire', label: 'Discovery Questionnaire' },
  { value: 'hld', label: 'HLD' },
  { value: 'lld', label: 'LLD' },
  { value: 'bom', label: 'BOM' },
  { value: 'risk_register', label: 'Risk Register' },
  { value: 'diagram', label: 'Diagram' },
  { value: 'config', label: 'Config' },
  { value: 'script', label: 'Script' },
  { value: 'checklist', label: 'Checklist' },
  { value: 'runbook', label: 'Runbook' },
  { value: 'terraform', label: 'Terraform' },
  { value: 'ansible', label: 'Ansible' },
  { value: 'qc_report', label: 'QC Report' },
  { value: 'qc_checklist', label: 'QC Checklist' },
  { value: 'qc_findings', label: 'QC Findings' },
  { value: 'standards_compliance_check', label: 'Standards Check' },
  { value: 'integrated_architecture', label: 'Integrated Architecture' },
  { value: 'combined_bom', label: 'Combined BOM' },
]

export default function EAArtifacts() {
  const { projectId } = useParams<{ projectId: string }>()
  const [phaseFilter, setPhaseFilter] = useState('all')
  const [vendorFilter, setVendorFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [sortBy, setSortBy] = useState('newest')

  const { data: project } = useQuery({
    queryKey: ['ea-project', projectId],
    queryFn: () => getEAProject(projectId!).then((res) => res.data),
    enabled: !!projectId,
  })

  const { data: artifactsData, isLoading } = useQuery({
    queryKey: ['ea-artifacts', projectId, phaseFilter, vendorFilter, typeFilter],
    queryFn: () =>
      getEAArtifacts(projectId!, {
        phase: phaseFilter !== 'all' ? phaseFilter : undefined,
        vendor: vendorFilter !== 'all' ? vendorFilter : undefined,
        artifact_type: typeFilter !== 'all' ? typeFilter : undefined,
      }).then((res) => res.data),
    enabled: !!projectId,
  })

  const artifacts: EAArtifactListItem[] = Array.isArray(artifactsData) ? artifactsData : []

  // Extract unique vendors from artifacts + project
  const availableVendors = useMemo(() => {
    const vendors = new Set<string>()
    if (project) project.vendors.forEach((v) => vendors.add(v))
    artifacts.forEach((a) => { if (a.vendor) vendors.add(a.vendor) })
    return ALL_VENDORS.filter((v) => vendors.has(v))
  }, [project, artifacts])

  const filteredArtifacts = useMemo(() => {
    const sorted = [...artifacts]
    sorted.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.filename.localeCompare(b.filename)
        case 'phase':
          return a.phase.localeCompare(b.phase)
        case 'vendor':
          return (a.vendor || '').localeCompare(b.vendor || '')
        default: // newest
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      }
    })
    return sorted
  }, [artifacts, sortBy])

  // Count artifacts per phase for tab badges
  const phaseCounts = useMemo(() => {
    const counts: Record<string, number> = { all: artifacts.length }
    artifacts.forEach((a) => {
      counts[a.phase] = (counts[a.phase] || 0) + 1
    })
    return counts
  }, [artifacts])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '2px solid rgba(59, 130, 246, 0.3)',
              boxShadow: '0 0 30px rgba(59, 130, 246, 0.3)',
            }}
          >
            <i className="fa-solid fa-spinner fa-spin text-2xl" style={{ color: '#60a5fa' }}></i>
          </div>
          <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Loading artifacts...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/ea/projects/${projectId}`}
            className="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-105"
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(100, 180, 255, 0.15)',
              color: 'rgba(148, 163, 184, 0.8)',
            }}
            aria-label="Back to project"
          >
            <i className="fa-solid fa-arrow-left"></i>
          </Link>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>Artifacts</h1>
            <p className="text-sm mt-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
              {project?.project_name || 'Loading...'}
            </p>
          </div>
        </div>
      </div>

      {/* Phase Filter Tabs */}
      <div className="flex items-center space-x-2 flex-wrap gap-y-2">
        {PHASE_TABS.map((tab) => {
          const isActive = phaseFilter === tab.value
          const color = tab.value === 'all' ? '#a78bfa' : PHASE_COLORS[tab.value] || '#94a3b8'
          const count = phaseCounts[tab.value] || 0
          return (
            <button
              key={tab.value}
              onClick={() => setPhaseFilter(tab.value)}
              className="px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2"
              style={{
                background: isActive
                  ? tab.value === 'all'
                    ? 'rgba(139, 92, 246, 0.25)'
                    : `${color}33`
                  : 'rgba(30, 41, 59, 0.5)',
                border: `1px solid ${isActive
                  ? tab.value === 'all'
                    ? 'rgba(139, 92, 246, 0.5)'
                    : `${color}66`
                  : 'rgba(100, 180, 255, 0.15)'}`,
                color: isActive ? color : 'rgba(148, 163, 184, 0.7)',
              }}
            >
              <span>{tab.label}</span>
              {count > 0 && (
                <span
                  className="text-xs px-1.5 py-0.5 rounded-full"
                  style={{
                    background: isActive ? `${color}33` : 'rgba(100, 116, 139, 0.2)',
                    color: isActive ? color : 'rgba(148, 163, 184, 0.5)',
                  }}
                >
                  {count}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Vendor Filter Buttons */}
      {availableVendors.length > 0 && (
        <div className="flex items-center space-x-2 flex-wrap gap-y-2">
          <span className="text-sm font-medium mr-1" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
            Vendor:
          </span>
          <button
            onClick={() => setVendorFilter('all')}
            className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200"
            style={{
              background: vendorFilter === 'all' ? 'rgba(139, 92, 246, 0.25)' : 'rgba(30, 41, 59, 0.5)',
              border: `1px solid ${vendorFilter === 'all' ? 'rgba(139, 92, 246, 0.5)' : 'rgba(100, 180, 255, 0.15)'}`,
              color: vendorFilter === 'all' ? '#a78bfa' : 'rgba(148, 163, 184, 0.7)',
            }}
          >
            All
          </button>
          {availableVendors.map((vendor) => {
            const color = VENDOR_COLORS[vendor] || '#94a3b8'
            const isActive = vendorFilter === vendor
            return (
              <button
                key={vendor}
                onClick={() => setVendorFilter(vendor)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200"
                style={{
                  background: isActive ? `${color}33` : 'rgba(30, 41, 59, 0.5)',
                  border: `1px solid ${isActive ? `${color}66` : 'rgba(100, 180, 255, 0.15)'}`,
                  color: isActive ? color : 'rgba(148, 163, 184, 0.7)',
                }}
              >
                {vendor}
              </button>
            )
          })}
        </div>
      )}

      {/* Filter Bar */}
      <div className="rounded-xl p-4" style={glassPanel}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
              Filters:
            </span>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-2 rounded-lg text-sm"
              style={{
                background: 'rgba(30, 41, 59, 0.5)',
                border: '1px solid rgba(100, 180, 255, 0.15)',
                color: '#e2e8f0',
              }}
              aria-label="Filter by artifact type"
            >
              {ARTIFACT_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center space-x-3">
            <span className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
              Sort by:
            </span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 rounded-lg text-sm"
              style={{
                background: 'rgba(30, 41, 59, 0.5)',
                border: '1px solid rgba(100, 180, 255, 0.15)',
                color: '#e2e8f0',
              }}
              aria-label="Sort artifacts"
            >
              <option value="newest">Newest First</option>
              <option value="name">Name</option>
              <option value="phase">Phase</option>
              <option value="vendor">Vendor</option>
            </select>
            <span className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
              {filteredArtifacts.length} artifact{filteredArtifacts.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Artifact Cards Grid */}
      {filteredArtifacts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredArtifacts.map((artifact) => (
            <EAArtifactCard key={artifact.id} artifact={artifact} projectId={projectId!} />
          ))}
        </div>
      ) : (
        <div className="rounded-xl p-12 text-center" style={glassPanel}>
          <div
            className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
            style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
            }}
          >
            <i
              className="fa-solid fa-file-lines text-3xl"
              style={{ color: 'rgba(96, 165, 250, 0.5)' }}
            ></i>
          </div>
          <h3 className="text-lg font-semibold mb-2" style={{ color: '#e2e8f0' }}>
            {phaseFilter !== 'all' || vendorFilter !== 'all' || typeFilter !== 'all'
              ? 'No artifacts match your filters'
              : 'No artifacts yet'}
          </h3>
          <p className="mb-6" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
            {phaseFilter !== 'all' || vendorFilter !== 'all' || typeFilter !== 'all'
              ? 'Try adjusting your filter criteria'
              : 'Artifacts will appear here as pipeline tasks generate them'}
          </p>
        </div>
      )}
    </div>
  )
}
